import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import baseUrl from "../../../../../api/baseUrl";
import CloseIcon from "@mui/icons-material/Close";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import Select from "react-select";
import { toast } from "react-toastify";
import SimpleToothChartPicker from "../treatments/SimpleToothChartPicker";
import {
  buildTreatmentPayload,
  getEarliestSubTreatmentDate,
  hasToothSelection,
  hasTreatmentSelection,
  isDateAfterDay,
  parseLocalDateInput,
  toLocalDateInputValue,
  type TreatmentFormData,
} from "./treatmentFormUtils";
import { appointmentIdPayload } from "../../../../../utils/appointmentLinkContext";

type FormDataType = TreatmentFormData;

const TREATMENT_STATUS_OPTIONS = [
  { value: "planned", label: "Planned" },
  { value: "ongoing", label: "Ongoing" },
  { value: "Complete", label: "Complete" },
];

const PLAN_STATUS_OPTIONS = [
  { value: "planned", label: "Plan" },
  { value: "ongoing", label: "Ongoing" },
  { value: "Complete", label: "Complete" },
];

const MODE_TOGGLE_NORMAL_WIDTH = 60;
const MODE_TOGGLE_PLAN_WIDTH = 108;

const ModeToggle = ({
  isTreatmentPlan,
  onChange,
}: {
  isTreatmentPlan: boolean;
  onChange: (enabled: boolean) => void;
}) => {
  return (
    <div
      className="relative inline-flex items-stretch rounded-full p-1 bg-red-50 dark:bg-slate-700 ring-1 ring-red-100 dark:ring-slate-600 text-[11px] font-semibold leading-none shrink-0 whitespace-nowrap select-none shadow-inner"
      role="group"
      aria-label="Treatment mode"
    >
      <span
        aria-hidden
        className="absolute top-1 bottom-1 left-1 rounded-full bg-gradient-to-r from-red-600 to-red-400 shadow-md transition-all duration-300 ease-out"
        style={{
          width: isTreatmentPlan
            ? MODE_TOGGLE_PLAN_WIDTH
            : MODE_TOGGLE_NORMAL_WIDTH,
          transform: isTreatmentPlan
            ? `translateX(${MODE_TOGGLE_NORMAL_WIDTH + 4}px)`
            : "translateX(0)",
        }}
      />
      <button
        type="button"
        onClick={() => onChange(false)}
        style={{ width: MODE_TOGGLE_NORMAL_WIDTH }}
        className={`relative z-10 flex items-center justify-center py-1.5 rounded-full whitespace-nowrap transition-colors duration-300 ${
          !isTreatmentPlan
            ? "text-white"
            : "text-red-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-slate-200"
        }`}
      >
        Normal
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        style={{ width: MODE_TOGGLE_PLAN_WIDTH }}
        className={`relative z-10 flex items-center justify-center py-1.5 rounded-full whitespace-nowrap transition-colors duration-300 ${
          isTreatmentPlan
            ? "text-white"
            : "text-red-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-slate-200"
        }`}
      >
        Treatment Plan
      </button>
    </div>
  );
};

const parseSelectedTeeth = (formData: FormDataType): string[] => {
  if (formData.toothNos) {
    return formData.toothNos
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (formData.toothNo) return [String(formData.toothNo)];
  return [];
};

const applySelectedTeeth = (teeth: string[]) => {
  const first = teeth[0] || "";
  return {
    toothNos: teeth.join(","),
    toothNo: /^\d+$/.test(first) ? first : "",
  };
};

function TreatmentFormModal({
  setaddTreatmentOpent,
  settreatment,
  treatment,
  fetchData,
  editItem,
  inline = false,
  inlineRow = false,
  embedded = false,
  onClose,
  defaultStatus = "Complete",
  deferSave = false,
  draftId,
  onDraftChange,
  onDraftRemove,
}: any) {
  const { ID } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = String(searchParams.get("appointmentId") ?? "").trim();
  const [openOther, setopenOther] = useState(false);
  const [otherserror, setotherserror] = useState(false);
  const [error, seterror] = useState(false);
  const [toothError, setToothError] = useState(false);
  const [data, setData] = useState([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const initialFormData: FormDataType = {
    treatmentkey: treatment.length + 1,
    treatment: "",
    treatments: "",
    treatmentId: "",
    type: "",
    types: "",
    typeId: "",
    subtype: "",
    subtypes: "",
    subtypeId: "",
    toothNos: "",
    toothNo: "",
    prices: 0,
    price: 0,
    description: "",
    createdAt: new Date(),
    quantity: 1,
    note: "",
    surface: "",
    status: defaultStatus,
    planStatus: "planned",
    draft: false,
  };

  const [formData, setformData] = useState(initialFormData);
  const [isTreatmentPlan, setIsTreatmentPlan] = useState(false);

  const setTreatmentPlanMode = (enabled: boolean) => {
    setIsTreatmentPlan(enabled);
    setformData((prev) => ({
      ...prev,
      status: enabled
        ? "long term"
        : prev.status === "long term"
          ? defaultStatus
          : prev.status,
      planStatus: enabled ? prev.planStatus || "planned" : prev.planStatus,
    }));
  };

  const setDraftMode = (enabled: boolean) => {
    setformData((prev) => ({
      ...prev,
      draft: enabled,
    }));
  };

  const getEmptyForm = (): FormDataType => ({
    treatmentkey: treatment.length + 1,
    treatment: "",
    treatments: "",
    treatmentId: "",
    type: "",
    types: "",
    typeId: "",
    subtype: "",
    subtypes: "",
    subtypeId: "",
    toothNos: "",
    toothNo: "",
    prices: 0,
    price: 0,
    description: "",
    createdAt: new Date(),
    quantity: 1,
    note: "",
    surface: "",
    status: defaultStatus,
    planStatus: "planned",
    draft: false,
  });

  const resetAddForm = () => {
    setformData(getEmptyForm());
    setIsTreatmentPlan(false);
    setopenOther(false);
    seterror(false);
    setotherserror(false);
    setToothError(false);
  };

  const closeAfterSubmit = () => {
    if (inline || inlineRow) {
      resetAddForm();
      onClose?.();
    } else {
      setaddTreatmentOpent(false);
    }
  };

  const handleInlineClose = () => {
    if (deferSave && draftId) {
      onDraftRemove?.(draftId);
      return;
    }
    resetAddForm();
    onClose?.();
    setaddTreatmentOpent?.(false);
  };

  useEffect(() => {
    if (deferSave && draftId && onDraftChange && !editItem) {
      onDraftChange(draftId, formData);
    }
  }, [deferSave, draftId, formData, onDraftChange, editItem]);

  // Searchable select styles (smaller size)
  const selectStyles = useMemo(
    () => ({
      control: (base: any, state: any) => ({
        ...base,
        minHeight: 34,
        borderRadius: "8px",
        borderColor: state.isFocused ? "var(--tw-ring-color, rgb(99 102 241))" : "rgb(226 232 240)",
        boxShadow: state.isFocused ? "0 0 0 2px rgb(99 102 241 / 0.2)" : "none",
        "&:hover": { borderColor: "rgb(203 213 225)" },
        backgroundColor: "white",
      }),
      menu: (base: any) => ({
        ...base,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
        zIndex: 50,
      }),
      menuList: (base: any) => ({
        ...base,
        padding: 0,
        maxHeight: 220,
      }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "rgb(99 102 241 / 0.15)"
          : state.isFocused
          ? "rgb(241 245 249)"
          : "white",
        color: state.isSelected ? "rgb(67 56 202)" : "rgb(30 41 59)",
        cursor: "pointer",
        fontSize: "0.8125rem",
        padding: "6px 12px",
      }),
      placeholder: (base: any) => ({
        ...base,
        color: "rgb(148 163 184)",
        fontSize: "0.8125rem",
      }),
      singleValue: (base: any) => ({
        ...base,
        color: "rgb(30 41 59)",
        fontSize: "0.8125rem",
      }),
      input: (base: any) => ({
        ...base,
        fontSize: "0.8125rem",
        color: "rgb(30 41 59)",
      }),
      indicatorSeparator: () => ({ display: "none" }),
      dropdownIndicator: (base: any) => ({
        ...base,
        color: "rgb(100 116 139)",
        padding: "6px 10px",
        "&:hover": { color: "rgb(71 85 105)" },
      }),
    }),
    []
  );

  const darkSelectStyles = useMemo(
    () => ({
      ...selectStyles,
      control: (base: any, state: any) => ({
        ...selectStyles.control(base, state),
        borderColor: state.isFocused ? "rgb(99 102 241)" : "rgb(71 85 105)",
        backgroundColor: "rgb(51 65 85)",
      }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "rgb(99 102 241 / 0.3)"
          : state.isFocused
          ? "rgb(51 65 85)"
          : "rgb(51 65 85)",
        color: state.isSelected ? "rgb(199 210 254)" : "rgb(226 232 240)",
      }),
      placeholder: (base: any) => ({ ...base, color: "rgb(148 163 184)" }),
      singleValue: (base: any) => ({ ...base, color: "rgb(226 232 240)" }),
      input: (base: any) => ({ ...base, color: "rgb(226 232 240)" }),
      menu: (base: any) => ({ ...base, backgroundColor: "rgb(51 65 85)" }),
      dropdownIndicator: (base: any) => ({ ...base, color: "rgb(148 163 184)" }),
    }),
    [selectStyles]
  );

  const treatmentOptions = useMemo(() => {
    const list = data?.filter((item: any) => item.status1 === true) || [];
    const others = list.filter((item: any) => (item.treatment || "").trim().toLowerCase() === "others");
    const rest = list.filter((item: any) => (item.treatment || "").trim().toLowerCase() !== "others");
    rest.sort((a: any, b: any) => (a.treatment || "").localeCompare(b.treatment || ""));
    const sorted = [...rest, ...others];
    return sorted.map((item: any) => ({
      value: item._id + "/" + item.treatment + "/" + (item.price ?? 0),
      label: item.treatment,
      price: Number(item.price ?? 0),
    }));
  }, [data]);

  const formatTreatmentOptionLabel = (option: { label: string; price?: number }) => (
    <div className="flex items-center justify-between gap-3 w-full">
      <span className="truncate">{option.label}</span>
      <span className="shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400 tabular-nums">
        Rs {Number(option.price ?? 0).toLocaleString()}
      </span>
    </div>
  );

  const typeOptions = useMemo(() => {
    const isOthers = (formData.treatment || "").trim().toLowerCase() === "others";
    if (!data || isOthers) return [];
    const types =
      data
        ?.filter((item: any) => String(item._id) === String(formData.treatmentId))
        ?.map((filteredItem: any) => filteredItem.type)
        ?.flat() || [];
    return types.map((item: any) => ({
      value: item._id + "/" + item.treatment + "/" + item.price,
      label: item.treatment,
    }));
  }, [data, formData.treatmentId, formData.treatment]);

  const subtypeOptions = useMemo(() => {
    const isOthers = (formData.treatment || "").trim().toLowerCase() === "others";
    if (!data || isOthers) return [];
    const subtypes =
      data
        ?.filter((item: any) => String(item._id) === String(formData.treatmentId))
        ?.map((filteredItem: any) => filteredItem.type)
        ?.flat()
        ?.filter((item: any) => String(item._id) === String(formData.typeId))
        ?.map((filteredItem: any) => filteredItem.subType)
        ?.flat() || [];
    return subtypes.map((item: any) => ({
      value: item._id + "/" + item.treatment + "/" + item.price,
      label: item.treatment,
    }));
  }, [data, formData.treatmentId, formData.typeId, formData.treatment]);

  useEffect(() => {
    baseUrl
      .get("alltreatment/findAllTreatment")
      .then((response) => {

        setData(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (!editItem) return;

    const quantity = Number(editItem.quantity) || 1;
    const unitPrice = Number(editItem.price) / quantity || 0;
    const catalogOthers = data?.find(
      (entry: any) =>
        (entry.treatment || "").trim().toLowerCase() === "others"
    ) as any;
    const isOthersItem =
      Boolean(catalogOthers?._id) &&
      String(editItem.treatmentId) === String(catalogOthers._id);

    setopenOther(isOthersItem);
    setIsTreatmentPlan(editItem.status === "long term");
    const treatmentId = editItem.treatmentId
      ? String(editItem.treatmentId)
      : "";
    setformData({
      treatmentkey: editItem.treatmentkey ?? treatment.length + 1,
      treatment: isOthersItem ? "others" : editItem.treatment || "",
      treatments: treatmentId
        ? `${treatmentId}/${isOthersItem ? "others" : editItem.treatment}/${unitPrice}`
        : "",
      treatmentId,
      type: editItem.type || "",
      types: editItem.typeId
        ? `${editItem.typeId}/${editItem.type}/${unitPrice}`
        : "",
      typeId: editItem.typeId ? String(editItem.typeId) : "",
      subtype: editItem.subtype || "",
      subtypes: editItem.subtypeId
        ? `${editItem.subtypeId}/${editItem.subtype}/${unitPrice}`
        : "",
      subtypeId: editItem.subtypeId ? String(editItem.subtypeId) : "",
      toothNos: editItem.toothNos || "",
      toothNo: editItem.toothNo != null ? String(editItem.toothNo) : "",
      prices: unitPrice,
      price: Number(editItem.price) || 0,
      description: isOthersItem
        ? editItem.treatment || editItem.description || ""
        : editItem.description || "",
      createdAt: editItem.createdAt ? new Date(editItem.createdAt) : new Date(),
      quantity,
      note: editItem.note || "",
      surface: editItem.surface || "",
      status: editItem.status || defaultStatus,
      planStatus: editItem.planStatus || "planned",
      draft: Boolean(editItem.draft),
    });
  }, [editItem, data, treatment.length, defaultStatus]);

  const earliestSubDate = useMemo(
    () => (editItem?._id ? getEarliestSubTreatmentDate(editItem) : null),
    [editItem]
  );
  const maxMainDateValue = earliestSubDate
    ? toLocalDateInputValue(earliestSubDate)
    : undefined;

  const onchange = (e: any) => {
    const { value, name } = e.target;

    if (name === "createdAt") {
      setformData((prev) => ({
        ...prev,
        createdAt: value ? parseLocalDateInput(value) : new Date(),
      }));
      return;
    }

    if (name === "treatments") {
      const isOthersFromApi = value && (value.split("/")[1] || "").trim().toLowerCase() === "others";
      if (isOthersFromApi) {
        const separatedArray = value.split("/");
        setopenOther(true);
        setformData((prevState) => ({
          ...prevState,
          [name]: value,
          treatmentId: separatedArray[0],
          treatment: separatedArray[1],
          prices: Number(separatedArray[2]) || 0,
          type: "",
          types: "",
          typeId: "",
          subtype: "",
          subtypes: "",
          subtypeId: "",
        }));
      } else {
        setopenOther(false);
        const separatedArray = value.split("/");
        setformData((prevState) => ({
          ...prevState,
          [name]: value,
          treatmentId: separatedArray[0],
          treatment: separatedArray[1],
          prices: Number(separatedArray[2]) || 0,
        }));
      }
    } else {
      if ((formData.treatment || "").trim().toLowerCase() === "others") {
        if (name === "types") {
          const separatedArray = value.split("/");
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            typeId: separatedArray[0],
            type: separatedArray[1],
            prices: separatedArray[2],
          }));
        } else if (name === "subtypes") {
          const separatedArray = value.split("/");
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            subtypeId: separatedArray[0],
            subtype: separatedArray[1],
            prices: separatedArray[2],
          }));
        } else if (name === "quantity") {
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            // price: formData.quantity
          }));
        } else if (name === "note") {
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto"; // Reset height
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set height to fit content
          }
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            // price: formData.quantity
          }));
        } else {
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
          }));
        }
      } else {
        if (name === "types") {
          const separatedArray = value.split("/");
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            typeId: separatedArray[0],
            type: separatedArray[1],
            prices: separatedArray[2],
          }));
        } else if (name === "subtypes") {
          const separatedArray = value.split("/");
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            subtypeId: separatedArray[0],
            subtype: separatedArray[1],
            prices: separatedArray[2],
          }));
        } else if (name === "quantity") {
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            // price: formData.quantity
          }));
        } else if (name === "prices") {
          setformData((prevState) => ({
            ...prevState,
            prices: value === "" ? 0 : Number(value) || 0,
          }));
        } else if (name === "note") {
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto"; // Reset height
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set height to fit content
          }
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            // price: formData.quantity
          }));
        } else {
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
          }));
        }
      }
    }
  };

  const onsubmit = async (e: any) => {
    e.preventDefault();
    if (!ID) return;

    const isEdit = Boolean(editItem?._id);
    if (!hasTreatmentSelection(formData, isEdit)) {
      seterror(true);
      return;
    }

    seterror(false);

    if (!hasToothSelection(formData)) {
      setToothError(true);
      toast.error("Select at least one tooth");
      return;
    }
    setToothError(false);

    const resolvedStatus = isTreatmentPlan ? "long term" : formData.status;
    const isOthers =
      (formData.treatment || "").trim().toLowerCase() === "others";

    if (isOthers && !formData.description) {
      setotherserror(true);
      return;
    }

    if (
      isEdit &&
      earliestSubDate &&
      isDateAfterDay(formData.createdAt, earliestSubDate)
    ) {
      toast.error(
        `Main treatment date cannot be later than its case treatments (${toLocalDateInputValue(
          earliestSubDate
        )})`
      );
      return;
    }

    setotherserror(false);
    const payload = {
      ...buildTreatmentPayload(formData, resolvedStatus),
      ...appointmentIdPayload(appointmentId),
    };

    try {
      if (isEdit) {
        const response = await baseUrl.post("treatment/update-treatments", payload, {
          headers: {
            treatmentid: ID,
            treatmentsid: String(editItem._id),
          },
        });
        toast.success(
          response?.data?.message ||
            (response?.data?.paymentSync
              ? "Paid treatment updated and payment synced"
              : "Treatment updated")
        );
        if (fetchData) {
          await fetchData();
        }
        closeAfterSubmit();
        return;
      }

      if (isOthers) {
        settreatment([...treatment, { ...formData, ...payload }]);
        await baseUrl.post("treatment/add-treatments", payload, {
          headers: { treatmentid: ID },
        });
      } else {
        const entry = { ...formData, ...payload };
        settreatment([...treatment, entry]);
        await baseUrl.post("treatment/add-treatments", payload, {
          headers: { treatmentid: ID },
        });
      }
      toast.success("Treatment added");
      closeAfterSubmit();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        (isEdit ? "Failed to update treatment" : "Failed to add treatment");
      toast.error(message);
      console.error(error);
    }
  };

  const formTitle = editItem?._id ? "Edit Treatment" : "Add Treatment";

  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const activeSelectStyles = isDark ? darkSelectStyles : selectStyles;
  const compactInputClass =
    "w-full min-w-0 px-2 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary";

  const inlineSelectStyles = useMemo(
    () => ({
      ...activeSelectStyles,
      control: (base: any, state: any) => ({
        ...activeSelectStyles.control(base, state),
        minHeight: 36,
        height: 36,
      }),
      valueContainer: (base: any) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      indicatorsContainer: (base: any) => ({
        ...base,
        height: 36,
      }),
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    }),
    [activeSelectStyles]
  );

  const menuPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed" as const,
  };

  const selectWrapClass = "min-w-0 w-full max-w-full";

  const showTypeField =
    (formData.treatment || "").trim().toLowerCase() !== "others" &&
    typeOptions.length > 0;
  const showSubtypeField =
    (formData.treatment || "").trim().toLowerCase() !== "others" &&
    subtypeOptions.length > 0;
  const showExtraRow = showTypeField || showSubtypeField || openOther;

  const handleRowSubmit = (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault();
    onsubmit(e);
  };

  if (inlineRow) {
    const isEdit = Boolean(editItem?._id);
    const fieldLabelClass =
      "block text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
    const fieldInputClass =
      "w-full h-9 px-2.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all";

    return (
      <>
        <tr className="bg-indigo-50/50 dark:bg-primary/10 border-t-2 border-indigo-200 dark:border-primary/30 align-top">
          <td colSpan={9} className="px-3 py-3">
            <div className="rounded-xl border border-indigo-200/80 dark:border-primary/30 bg-white/95 dark:bg-slate-900/70 shadow-sm overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 border-b border-indigo-100 dark:border-slate-700/80 bg-gradient-to-r from-indigo-50/90 to-white dark:from-primary/15 dark:to-slate-900/40">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 dark:bg-primary/20 text-indigo-700 dark:text-indigo-300 shrink-0">
                    <MedicalServicesIcon sx={{ fontSize: 16 }} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 leading-tight">
                      {isEdit ? "Edit treatment" : "New treatment"}
                    </p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">
                      Fill details, then select teeth
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <ModeToggle
                    isTreatmentPlan={isTreatmentPlan}
                    onChange={setTreatmentPlanMode}
                  />
                  <label
                    className={`h-8 inline-flex items-center gap-1.5 px-2.5 rounded-lg text-[11px] font-semibold cursor-pointer border transition-colors ${
                      formData.draft
                        ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-700/50"
                        : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(formData.draft)}
                      onChange={(e) => setDraftMode(e.target.checked)}
                      className="sr-only"
                    />
                    Draft
                  </label>
                  <div className="flex items-center gap-1.5">
                    {!deferSave && (
                      <button
                        type="button"
                        onClick={handleRowSubmit}
                        className="h-8 px-3 text-[11px] rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold whitespace-nowrap hover:shadow-md hover:shadow-primary/20 transition-all"
                      >
                        {isEdit ? "Update" : "Add"}
                      </button>
                    )}
                    {deferSave && !isEdit && (
                      <span className="h-8 inline-flex items-center px-2.5 text-[11px] rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-semibold whitespace-nowrap">
                        Pending
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleInlineClose}
                      className="h-8 px-3 text-[11px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium whitespace-nowrap hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      {deferSave && !isEdit ? "Remove" : "Cancel"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-3.5 space-y-3">
                <div className="grid grid-cols-12 gap-2.5">
                  <div className="col-span-12 sm:col-span-4 lg:col-span-2">
                    <label className={fieldLabelClass}>Date</label>
                    <input
                      type="date"
                      name="createdAt"
                      value={toLocalDateInputValue(formData.createdAt)}
                      max={maxMainDateValue}
                      onChange={onchange}
                      className={fieldInputClass}
                      title={
                        maxMainDateValue
                          ? `Cannot be later than earliest case treatment (${maxMainDateValue})`
                          : "Treatment date"
                      }
                    />
                  </div>

                  <div className="col-span-12 sm:col-span-6 lg:col-span-3 min-w-0">
                    <label className={fieldLabelClass}>
                      Treatment <span className="text-red-500 normal-case">*</span>
                    </label>
                    <div className={selectWrapClass}>
                      <Select
                        isSearchable
                        placeholder="Select treatment"
                        options={treatmentOptions}
                        formatOptionLabel={formatTreatmentOptionLabel}
                        value={
                          treatmentOptions.find(
                            (o) => o.value === formData.treatments
                          ) ?? null
                        }
                        onChange={(option: any) =>
                          option &&
                          onchange({
                            target: { name: "treatments", value: option.value },
                          })
                        }
                        styles={inlineSelectStyles}
                        classNamePrefix="react-select"
                        {...menuPortalProps}
                      />
                    </div>
                    {error && formData.treatments === "" && (
                      <p className="text-[10px] text-red-500 mt-1">Required</p>
                    )}
                  </div>

                  <div className="col-span-4 sm:col-span-2 lg:col-span-1">
                    <label className={fieldLabelClass}>Qty</label>
                    <input
                      value={formData.quantity}
                      name="quantity"
                      type="number"
                      min={1}
                      onChange={onchange}
                      className={fieldInputClass}
                    />
                  </div>

                  <div className="col-span-8 sm:col-span-4 lg:col-span-2">
                    <label className={fieldLabelClass}>Price</label>
                    <input
                      value={formData.prices ? formData.prices : ""}
                      name="prices"
                      type="number"
                      min={0}
                      placeholder="Optional"
                      onChange={onchange}
                      className={fieldInputClass}
                    />
                  </div>

                  <div className="col-span-12 sm:col-span-4 lg:col-span-2">
                    <label className={fieldLabelClass}>
                      {isTreatmentPlan ? "Plan status" : "Status"}
                    </label>
                    {isTreatmentPlan ? (
                      <select
                        value={formData.planStatus || "planned"}
                        name="planStatus"
                        onChange={onchange}
                        className={`${fieldInputClass} cursor-pointer`}
                      >
                        {PLAN_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={formData.status}
                        name="status"
                        onChange={onchange}
                        className={`${fieldInputClass} cursor-pointer`}
                      >
                        {TREATMENT_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="col-span-12 sm:col-span-8 lg:col-span-2">
                    <label className={fieldLabelClass}>Note</label>
                    <input
                      value={formData.note}
                      name="note"
                      type="text"
                      placeholder="Optional note"
                      onChange={onchange}
                      className={fieldInputClass}
                    />
                  </div>
                </div>

                {showExtraRow && (
                  <div className="grid grid-cols-12 gap-2.5">
                    {showTypeField && (
                      <div className="col-span-12 sm:col-span-6 min-w-0">
                        <label className={fieldLabelClass}>Type</label>
                        <Select
                          isSearchable
                          placeholder="Select type"
                          options={typeOptions}
                          value={
                            typeOptions.find((o) => o.value === formData.types) ??
                            null
                          }
                          onChange={(option: any) =>
                            option &&
                            onchange({
                              target: { name: "types", value: option.value },
                            })
                          }
                          styles={inlineSelectStyles}
                          classNamePrefix="react-select"
                          {...menuPortalProps}
                        />
                      </div>
                    )}
                    {showSubtypeField && (
                      <div className="col-span-12 sm:col-span-6 min-w-0">
                        <label className={fieldLabelClass}>Subtype</label>
                        <Select
                          isSearchable
                          placeholder="Select subtype"
                          options={subtypeOptions}
                          value={
                            subtypeOptions.find(
                              (o) => o.value === formData.subtypes
                            ) ?? null
                          }
                          onChange={(option: any) =>
                            option &&
                            onchange({
                              target: { name: "subtypes", value: option.value },
                            })
                          }
                          styles={inlineSelectStyles}
                          classNamePrefix="react-select"
                          {...menuPortalProps}
                        />
                      </div>
                    )}
                    {openOther && (
                      <div className="col-span-12 min-w-0">
                        <label className={fieldLabelClass}>
                          Description <span className="text-red-500 normal-case">*</span>
                        </label>
                        <input
                          value={formData.description}
                          name="description"
                          type="text"
                          placeholder="Enter treatment description"
                          onChange={onchange}
                          className={fieldInputClass}
                        />
                        {otherserror && formData.description === "" && (
                          <p className="text-[10px] text-red-500 mt-1">Required</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="pt-1 border-t border-slate-100 dark:border-slate-700/80">
                  <SimpleToothChartPicker
                    value={parseSelectedTeeth(formData)}
                    required
                    error={toothError}
                    onChange={(teeth) => {
                      setToothError(false);
                      setformData((prev) => ({
                        ...prev,
                        ...applySelectedTeeth(teeth),
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </td>
        </tr>
      </>
    );
  }

  const formBody = (
        <form onSubmit={onsubmit} className={inline ? "space-y-3" : "flex-1 overflow-y-auto p-5 space-y-3"}>
          {/* Treatment Select */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Select Treatment *</label>
            <Select
              isSearchable
              placeholder="Select treatment"
              options={treatmentOptions}
              formatOptionLabel={formatTreatmentOptionLabel}
              value={treatmentOptions.find((o) => o.value === formData.treatments) ?? null}
              onChange={(option: any) =>
                option &&
                onchange({
                  target: { name: "treatments", value: option.value },
                })
              }
              styles={typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? darkSelectStyles : selectStyles}
              classNamePrefix="react-select"
              classNames={{
                control: () => "!rounded-lg !border-slate-200 dark:!border-slate-600 !bg-white dark:!bg-slate-700",
                menu: () => "!rounded-lg",
              }}
            />
            {error && formData.treatments === "" && (
              <p className="text-xs text-red-500 mt-0.5">Treatment is required</p>
            )}
          </div>

          {/* Type Select */}
          {(formData.treatment || "").trim().toLowerCase() !== "others" && typeOptions.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Select Type</label>
              <Select
                isSearchable
                placeholder="Select type"
                options={typeOptions}
                value={typeOptions.find((o) => o.value === formData.types) ?? null}
                onChange={(option: any) =>
                  option && onchange({ target: { name: "types", value: option.value } })
                }
                styles={typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? darkSelectStyles : selectStyles}
                classNamePrefix="react-select"
                classNames={{
                  control: () => "!rounded-lg !border-slate-200 dark:!border-slate-600 !bg-white dark:!bg-slate-700",
                  menu: () => "!rounded-lg",
                }}
              />
            </div>
          )}

          {/* Subtype Select */}
          {(formData.treatment || "").trim().toLowerCase() !== "others" && subtypeOptions.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Select Subtype</label>
              <Select
                isSearchable
                placeholder="Select subtype"
                options={subtypeOptions}
                value={subtypeOptions.find((o) => o.value === formData.subtypes) ?? null}
                onChange={(option: any) =>
                  option && onchange({ target: { name: "subtypes", value: option.value } })
                }
                styles={typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? darkSelectStyles : selectStyles}
                classNamePrefix="react-select"
                classNames={{
                  control: () => "!rounded-lg !border-slate-200 dark:!border-slate-600 !bg-white dark:!bg-slate-700",
                  menu: () => "!rounded-lg",
                }}
              />
            </div>
          )}

          {/* Treatment date */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Date
            </label>
            <input
              type="date"
              name="createdAt"
              value={toLocalDateInputValue(formData.createdAt)}
              max={maxMainDateValue}
              onChange={onchange}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              title={
                maxMainDateValue
                  ? `Cannot be later than earliest case treatment (${maxMainDateValue})`
                  : "Treatment date"
              }
            />
          </div>

          {/* Price & Quantity — price is optional */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                Price
              </label>
              <input
                value={formData.prices ? formData.prices : ""}
                name="prices"
                type="number"
                min={0}
                placeholder="Optional"
                onChange={onchange}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Quantity</label>
              <input
                value={formData.quantity}
                name="quantity"
                type="number"
                onChange={onchange}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
          </div>

          {/* Description (for Others) */}
          {openOther && (
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Description *</label>
              <input
                value={formData.description}
                name="description"
                type="text"
                onChange={onchange}
                placeholder="Enter treatment description"
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
              {otherserror && formData.description === "" && (
                <p className="text-xs text-red-500 mt-0.5">Description is required for others</p>
              )}
            </div>
          )}

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              {isTreatmentPlan ? "Plan Status" : "Status"}
            </label>
            {isTreatmentPlan ? (
              <select
                value={formData.planStatus || "planned"}
                name="planStatus"
                onChange={onchange}
                className={`w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${
                  formData.planStatus === "Complete"
                    ? "text-emerald-700 dark:text-emerald-300"
                    : formData.planStatus === "ongoing"
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-violet-700 dark:text-violet-300"
                }`}
              >
                {PLAN_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={formData.status}
                name="status"
                onChange={onchange}
                className={`w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${
                  formData.status === "Complete"
                    ? "text-emerald-700 dark:text-emerald-300"
                    : formData.status === "ongoing"
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-slate-800 dark:text-slate-200"
                }`}
              >
                {TREATMENT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Tooth Chart */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Tooth Selection <span className="text-red-500">*</span>
            </label>
            <SimpleToothChartPicker
              value={parseSelectedTeeth(formData)}
              required
              error={toothError}
              onChange={(teeth) => {
                setToothError(false);
                setformData((prev) => ({ ...prev, ...applySelectedTeeth(teeth) }));
              }}
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Note</label>
            <textarea
              ref={textareaRef}
              value={formData.note}
              name="note"
              onChange={onchange}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
            />
          </div>

          {/* Submit / Cancel */}
          <div className={`flex gap-2 ${inline ? "" : ""}`}>
            {inline && (
              <button
                type="button"
                onClick={handleInlineClose}
                className="flex-1 py-2.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className={`${inline ? "flex-1" : "w-full"} py-2.5 text-sm rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all`}
            >
              {editItem?._id ? "Update Treatment" : "Add Treatment"}
            </button>
          </div>
        </form>
  );

  if (inline && embedded) {
    return (
      <>
        <div className="flex items-center justify-between mb-2 gap-2">
          <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
            {formTitle}
          </h4>
          <div className="flex items-center gap-2">
            <ModeToggle
              isTreatmentPlan={isTreatmentPlan}
              onChange={setTreatmentPlanMode}
            />
            <label
              className={`h-7 inline-flex items-center px-2 rounded-lg text-[11px] font-semibold cursor-pointer border transition-colors ${
                formData.draft
                  ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-700/50"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
              }`}
            >
              <input
                type="checkbox"
                checked={Boolean(formData.draft)}
                onChange={(e) => setDraftMode(e.target.checked)}
                className="sr-only"
              />
              Draft
            </label>
            <button
              type="button"
              onClick={handleInlineClose}
              className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-600/60 flex items-center justify-center transition-colors"
              title="Close"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
        {formBody}
      </>
    );
  }

  if (inline) {
    return (
      <tr className="bg-indigo-50/60 dark:bg-primary/10 border-t-2 border-indigo-200 dark:border-primary/30">
        <td colSpan={9} className="p-3">
          <div className="flex items-center justify-between mb-2 gap-2">
            <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
              {formTitle}
            </h4>
            <div className="flex items-center gap-2">
              <ModeToggle
                isTreatmentPlan={isTreatmentPlan}
                onChange={setTreatmentPlanMode}
              />
              <label
                className={`h-7 inline-flex items-center px-2 rounded-lg text-[11px] font-semibold cursor-pointer border transition-colors ${
                  formData.draft
                    ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-700/50"
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                }`}
              >
                <input
                  type="checkbox"
                  checked={Boolean(formData.draft)}
                  onChange={(e) => setDraftMode(e.target.checked)}
                  className="sr-only"
                />
                Draft
              </label>
              <button
                type="button"
                onClick={handleInlineClose}
                className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-600/60 flex items-center justify-center transition-colors"
                title="Close"
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
          </div>
          {formBody}
        </td>
      </tr>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-primary to-primaryLight gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <MedicalServicesIcon className="text-white shrink-0" />
            <h2 className="text-xl font-bold text-white truncate">{formTitle}</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ModeToggle
              isTreatmentPlan={isTreatmentPlan}
              onChange={setTreatmentPlanMode}
            />
            <label
              className={`h-8 inline-flex items-center px-2.5 rounded-lg text-[11px] font-semibold cursor-pointer border transition-colors ${
                formData.draft
                  ? "bg-amber-100 text-amber-800 border-amber-200"
                  : "bg-white/15 text-white border-white/30 hover:bg-white/25"
              }`}
            >
              <input
                type="checkbox"
                checked={Boolean(formData.draft)}
                onChange={(e) => setDraftMode(e.target.checked)}
                className="sr-only"
              />
              Draft
            </label>
            <button
              onClick={() => setaddTreatmentOpent(false)}
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
          </div>
        </div>
        {formBody}
      </div>
    </div>
  );
}

export default TreatmentFormModal;
